import { ENVIRONMENT as env } from '@updater-core/lib/environment';
import fetch from '@updater-core/lib/fetch';
import { ERROR, track } from '@updater-core/lib/tracker';
import { ITEM_STATUSES, ITEM_KINDS } from '@updater-core/constants/items';
import SessionInteractor from '@updater-core/interactors/shell/session';

export function startItem({
  code,
  moveId,
  headers,
}: {
  code: ITEM_KINDS;
  moveId: number;
  headers: Object;
}) {
  const url = `/items`;

  const body = {
    move_id: moveId,
    kind: code,
  };

  return makeRequest(url, { body, headers });
}

export function getItems({ moveId, headers }) {
  const url = `/items?filter[move_id]=${moveId}`;

  return makeRequest(url, {
    headers,
    method: 'GET',
  });
}
export function setItemReserved({ itemId, headers }) {
  const url = `/items/${itemId}`;

  const body = {
    status: ITEM_STATUSES.RESERVED,
  };

  return makeRequest(url, { headers, body, method: 'PATCH' });
}
export function setItemComplete({ itemId, headers, metadata }) {
  const url = `/items/${itemId}`;

  const body = {
    status: ITEM_STATUSES.COMPLETED,
    metadata,
  };

  return makeRequest(url, { headers, body, method: 'PATCH' });
}

export function setMetadata({ headers, moveId, kind, metadata }) {
  const url = `/items`;
  const body = {
    metadata,
    move_id: moveId,
    kind,
  };

  return makeRequest(url, { headers, body, method: 'POST' });
}

export function markItemStatusOnMove({ code, moveId, headers, status }) {
  const url = `/moves/${moveId}/${code}/${status}`;
  return makeRequest(url, { headers });
}

export function setItemMetadataAndStatus({
  headers,
  moveId,
  kind,
  status,
  metadata,
}) {
  const url = '/items';
  const body = {
    metadata,
    move_id: moveId,
    kind,
    status,
  };

  return makeRequest(url, { headers, body, method: 'POST' });
}

export type MoveItemsGateway = ReturnType<typeof createGateway>;

export function createGateway({
  interactors: { session },
}: {
  interactors: { session: typeof SessionInteractor };
}) {
  function moveId() {
    const sessionState = session.getState();
    return session.selectors.currentMoveId(sessionState);
  }

  function requestHeaders() {
    const sessionState = session.getState();
    return session.selectors.requestHeaders(sessionState);
  }

  return {
    items: () =>
      getItems({
        moveId: moveId(),
        headers: requestHeaders(),
      }),
    itemsWithCode: (code: string) =>
      getItems({
        moveId: moveId(),
        headers: requestHeaders(),
      }).then((items) => items.filter(({ kind }) => kind === code)),
    startItem: (code: ITEM_KINDS) =>
      startItem({
        code,
        moveId: moveId(),
        headers: requestHeaders(),
      }),
    reserveItem: (id: number) =>
      setItemReserved({
        itemId: id,
        headers: requestHeaders(),
      }),
    completeItem: (id: number, metadata = {}) =>
      setItemComplete({
        itemId: id,
        headers: requestHeaders(),
        metadata,
      }),

    setItemMetadata: ({ kind, metadata = {} }) =>
      setMetadata({
        headers: requestHeaders(),
        metadata,
        moveId: moveId(),
        kind,
      }),
    markItemStatusOnMove: (code: ITEM_KINDS, status: ITEM_STATUSES) =>
      markItemStatusOnMove({
        code,
        status,
        moveId: moveId(),
        headers: requestHeaders(),
      }),
    setItemMetadataAndStatus: (
      kind: ITEM_KINDS,
      status: ITEM_STATUSES,
      metadata: {}
    ) =>
      setItemMetadataAndStatus({
        kind,
        status,
        metadata,
        headers: requestHeaders(),
        moveId: moveId(),
      }),
    // TEMP: Since we do not need to care about "starting"
    // Items for quick setup, we are only starting them
    // in order to complete them thereafter.
    // In the future this logic can be lifted if consumers
    // Will be keeping track of items
    startAndCompleteItem: (code: ITEM_KINDS, metadata = {}) => {
      // eslint-disable-next-line no-underscore-dangle
      const _moveId = moveId();
      // eslint-disable-next-line no-underscore-dangle
      const _headers = requestHeaders();
      return startItem({
        code,
        moveId: _moveId,
        headers: _headers,
      }).then((response) => {
        const itemId = response?.id;
        return setItemComplete({
          itemId,
          headers: _headers,
          metadata,
        });
      });
    },
  };
}

function handleError(source) {
  return (ex) => {
    if (window.console && window.console.error) {
      console.error(ex); // eslint-disable-line no-console
    }
    track({
      domain: 'quick_setup',
      verb: ERROR,
      object: `${source}_fetch_error`,
      details: {
        message: ex.message,
        stack: ex.stack,
        ...ex,
      },
    });
  };
}

function makeRequest(
  path: string,
  {
    headers,
    method = 'POST',
    body,
  }: { headers: any; method?: string; body?: any }
) {
  return fetch(`${env.endpoints.api.url}/v2${path}`, {
    method,
    mode: 'cors',
    headers: {
      'content-type': 'application/json; charset=utf-8',
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
    .then((response) => response.json())
    .catch(handleError('move_items_gateway'));
}
